import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PaymentMethodSummary } from '@aeris/contracts'
import { formatPercent, parseHumanAmount, toWire } from '@aeris/money'
import {
  PAYMENT_METHOD_KINDS,
  PAYMENT_METHOD_KIND_LABELS,
  allowsChange,
  defaultGeneratesReceivable,
  defaultSettlement,
  isValidMethodCode,
  normalizeMethodCode,
  supportsInstalments,
  type PaymentMethodKind,
} from '@aeris/payments'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  MoneyInput,
  Select,
} from '@aeris/ui'

import {
  ApiError,
  createPaymentMethod,
  updatePaymentMethod,
} from '../lib/api.ts'

/**
 * Create and edit a payment method.
 *
 * The rules are validated in the browser by the same functions the server uses
 * — `@aeris/payments` is imported by both — so a card method that cannot give
 * change is refused before a round trip, and refused again server-side
 * regardless. Master Specification section 28: never trust the frontend.
 *
 * The **kind cannot be changed** once a method exists. A method that was cash
 * and became a card is a different method, and re-kinding one would make every
 * payment already taken with it read as something it was not.
 */

export type PaymentMethodFormProps = {
  /** Absent means "create". */
  method?: PaymentMethodSummary | undefined
  onClose: () => void
}

/** `3.490000` from the wire is `3,49` in a field a person edits. */
const feeToField = (value: string): string => {
  const trimmed = value.replace(/0+$/, '').replace(/\.$/, '')
  return (trimmed.length === 0 ? '0' : trimmed).replace('.', ',')
}

export const PaymentMethodForm = ({
  method,
  onClose,
}: PaymentMethodFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const editing = method !== undefined

  const [kind, setKind] = useState<PaymentMethodKind>(method?.kind ?? 'cash')
  const [code, setCode] = useState(method?.code ?? '')
  const [name, setName] = useState(method?.name ?? '')
  const [fee, setFee] = useState(feeToField(method?.feePercent ?? '0'))
  const [settlementDays, setSettlementDays] = useState(
    String(method?.settlementDays ?? defaultSettlement('cash').settlementDays)
  )
  const [maxInstalments, setMaxInstalments] = useState(
    String(method?.maxInstalments ?? 1)
  )
  /*
   * The two scenarios, and which one this method is.
   *
   * ADR-0021 left "should a sale on deferred terms raise a receivable?" open;
   * the answer is BOTH, chosen here, before the fact and editable afterwards.
   * A new method starts from `defaultGeneratesReceivable` — true for fiado,
   * which is a debt from the first second, and false for everything else.
   */
  const [generatesReceivable, setGeneratesReceivable] = useState(
    method?.generatesReceivable ?? defaultGeneratesReceivable(kind)
  )
  const [receivableDueDays, setReceivableDueDays] = useState(
    String(method?.receivableDueDays ?? 30)
  )
  const [error, setError] = useState<string | null>(null)

  /**
   * Choosing a kind fills in what that kind ordinarily does.
   *
   * Fills in, not locks: the numbers are the ordinary Brazilian arrangement —
   * débito em D+1, crédito em 30 dias — and every one of them is negotiated per
   * acquirer. A shop with a fourteen-day contract edits the field and the edit
   * sticks.
   */
  const chooseKind = (next: PaymentMethodKind): void => {
    setKind(next)
    setSettlementDays(String(defaultSettlement(next).settlementDays))
    if (!supportsInstalments(next)) setMaxInstalments('1')
    if (name.trim().length === 0) setName(PAYMENT_METHOD_KIND_LABELS[next])
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const normalized = normalizeMethodCode(code)

      if (!isValidMethodCode(normalized)) {
        throw new ApiError(
          422,
          'invalid_payment_method_code',
          'Use letras, números, ponto, hífen, barra ou sublinhado.'
        )
      }
      if (name.trim().length === 0) {
        throw new ApiError(422, 'invalid_name', 'Informe o nome.')
      }

      // Parsed with @aeris/money, never with parseFloat: a rate that becomes a
      // JavaScript number is a rate that has already lost the argument.
      const feePercent = parseHumanAmount(fee, 6)
      if (feePercent === null) {
        throw new ApiError(
          422,
          'invalid_fee',
          'Informe a taxa, por exemplo 3,49.'
        )
      }

      const days = Number.parseInt(settlementDays, 10)
      if (!Number.isInteger(days) || days < 0) {
        throw new ApiError(
          422,
          'invalid_settlement_days',
          'Informe em quantos dias o dinheiro cai na conta.'
        )
      }

      const instalments = Number.parseInt(maxInstalments, 10)
      if (!Number.isInteger(instalments) || instalments < 1) {
        throw new ApiError(
          422,
          'invalid_instalments',
          'O número de parcelas começa em 1.'
        )
      }

      const dueDays = Number.parseInt(receivableDueDays, 10)
      if (generatesReceivable && (!Number.isInteger(dueDays) || dueDays < 0)) {
        throw new ApiError(
          422,
          'invalid_receivable_days',
          'Informe em quantos dias vence a primeira parcela.'
        )
      }

      return method === undefined
        ? await createPaymentMethod({
            code: normalized,
            name: name.trim(),
            kind,
            settlesImmediately: days === 0,
            settlementDays: days,
            feePercent: toWire(feePercent),
            allowsChange: allowsChange(kind),
            maxInstalments: instalments,
            generatesReceivable,
            receivableDueDays: dueDays,
          })
        : await updatePaymentMethod(method.id, {
            // The version this form was opened with. If somebody else saved in
            // the meantime the server refuses rather than overwriting them.
            version: method.version,
            code: normalized,
            name: name.trim(),
            settlesImmediately: days === 0,
            settlementDays: days,
            feePercent: toWire(feePercent),
            maxInstalments: instalments,
            generatesReceivable,
            receivableDueDays: dueDays,
          })
    },
    onSuccess: () => {
      /*
       * Closed as soon as the server accepted, and the lists refreshed after.
       *
       * It used to await `invalidateQueries` first, which awaits the refetch of
       * every active query it touches — so the dialog stayed open, with its
       * Salvar button spinning, for as long as an unrelated list screen took to
       * reload. The person's work was finished at the 201; making them watch a
       * background refresh is the dialog reporting somebody else's latency as
       * its own.
       */
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={
          editing ? 'Editar forma de pagamento' : 'Nova forma de pagamento'
        }
        description="Alterações são registradas na auditoria."
        className="max-w-lg"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            mutation.mutate()
          }}
        >
          {error === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {error}
            </p>
          )}

          <Field
            label="Tipo"
            required
            hint={
              editing
                ? 'O tipo não muda: os pagamentos já registrados guardam o tipo do dia em que foram feitos.'
                : 'Define troco, parcelamento e prazo de recebimento.'
            }
          >
            {(field) => (
              <Select
                {...field}
                value={kind}
                disabled={editing}
                onChange={(event) => {
                  chooseKind(event.target.value as PaymentMethodKind)
                }}
              >
                {PAYMENT_METHOD_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {PAYMENT_METHOD_KIND_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Nome" required>
            {(field) => (
              <Input
                {...field}
                value={name}
                autoFocus
                onChange={(event) => {
                  setName(event.target.value)
                }}
                placeholder="Cartão de crédito"
              />
            )}
          </Field>

          <Field label="Código" required hint="Convertido para maiúsculas">
            {(field) => (
              <Input
                {...field}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value)
                }}
                placeholder="CREDITO"
                className="font-(--font-mono)"
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Taxa (%)"
              hint="A taxa da adquirente. 0 se não houver."
            >
              {(field) => (
                <MoneyInput
                  symbol={false}
                  {...field}
                  value={fee}
                  onValueChange={setFee}
                />
              )}
            </Field>

            <Field label="Recebimento (dias)" hint="0 é à vista, na hora.">
              {(field) => (
                <Input
                  {...field}
                  value={settlementDays}
                  inputMode="numeric"
                  onChange={(event) => {
                    setSettlementDays(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>

          <Field
            label="Máximo de parcelas"
            hint={
              supportsInstalments(kind)
                ? 'Quantas parcelas esta forma aceita.'
                : 'Esta forma é um pagamento único e não parcela.'
            }
          >
            {(field) => (
              <Input
                {...field}
                value={maxInstalments}
                inputMode="numeric"
                disabled={!supportsInstalments(kind)}
                onChange={(event) => {
                  setMaxInstalments(event.target.value)
                }}
              />
            )}
          </Field>

          {/* The switch that decides whether being paid this way leaves the
              shop owed money. Per method rather than per shop, because fiado,
              boleto and dinheiro do not agree and one answer for all three
              would be wrong for two of them. */}
          <div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2">
            <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text-secondary)">
              <input
                type="checkbox"
                checked={generatesReceivable}
                onChange={(event) => {
                  setGeneratesReceivable(event.target.checked)
                }}
              />
              Gera contas a receber
            </label>

            {generatesReceivable ? (
              <Field
                label="Vencimento da 1ª parcela (dias)"
                hint="Contados da venda. O número de parcelas vem do que o cliente combinou no caixa, não daqui."
                className="w-64"
              >
                {(field) => (
                  <Input
                    {...field}
                    value={receivableDueDays}
                    inputMode="numeric"
                    onChange={(event) => {
                      setReceivableDueDays(event.target.value)
                    }}
                  />
                )}
              </Field>
            ) : (
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Uma venda paga assim fecha na hora e não deixa título em aberto.
              </p>
            )}
          </div>

          {/* Not a control: only cash gives change, and a checkbox somebody can
              tick and have refused teaches nothing. Stated, so the rule is
              visible where it applies. */}
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {allowsChange(kind)
              ? 'Dá troco.'
              : 'Não dá troco — só é possível dar troco em dinheiro.'}
            {fee.trim() === '0' || fee.trim().length === 0
              ? ''
              : ` Taxa atual: ${formatPercent(parseHumanAmount(fee, 6) ?? { units: 0n, scale: 6 })}.`}
          </p>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
